/*
  Лицевые счета выбранного дома и люди, с которых взыскивают.

  🔴 Указание владельца 14.09.2026: «по дому - его лицевые счета и должников».
  До этого узнать состав должников дома можно было только перебором карточек
  счетов по одной.

  Имена и роль - всё, что здесь показывается о человеке. Паспорт, телефон и
  прочие сведения живут на карточке счёта, где ими заполняют заявление: обзорный
  экран, читаемый целиком, не место для персональных данных всего дома.

  Счета грузит `useHouseAccounts`: страницы режет сервер (21.10.2026), и под таблицей
  общая панель с числом счетов.
*/

import { Link } from 'react-router-dom'

import { PERSON_ROLE_TITLES, money, percent } from '../api/format.js'
import { Empty, Loading, Notice, Tag } from './Common.jsx'
import PageBar from './PageBar.jsx'
import { useHouseAccounts } from './useHouses.js'

export default function HouseAccounts({ address }) {
  // 21.10.2026: счетов в доме бывает под сотню - страницы режет сервер. Хук стоит до
  // раннего возврата: пока счета грузятся, `accounts` равен null.
  const { rows: accounts, error, bar } = useHouseAccounts(address)
  if (error) return <Notice error={error} />
  if (accounts === null) return <Loading what={`Читаем счета дома «${address}»`} />

  return (
    <section className="card">
      <h2 className="section__title">Счета дома «{address}»</h2>

      {accounts.length === 0 ? (
        <Empty title="Под этот дом не подошло ни одного счёта">
          Проверьте написание адреса: Арбитр ЖКХ сравнивает его с адресами помещений из вашей
          выгрузки. Ноль счетов обычно означает, что дом записан иначе, чем его пишет биллинг.
        </Empty>
      ) : (
        <>
          <table className="table table--fixed table--house-accounts">
            <thead>
              <tr>
                <th>Счёт</th>
                <th>Помещение</th>
                <th>Должники</th>
                <th>Стадия</th>
                <th className="table__num">К взысканию</th>
              </tr>
            </thead>
            <tbody>
              {accounts.map((account) => (
                <tr key={account.account_id}>
                  <td>
                    <Link to={`/accounts/${account.account_id}`}>{account.number}</Link>
                  </td>
                  <td data-label="Помещение">{account.address}</td>
                  <td className="table__block" data-label="Должники">
                    <Debtors debtors={account.debtors} />
                  </td>
                  <td className="table__block" data-label="Стадия">
                    <Tag kind="muted">{account.stage_title}</Tag>
                  </td>
                  <td className="table__num" data-label="К взысканию">
                    {money(account.claim_amount)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <PageBar {...bar} what="счетов" />
        </>
      )}
    </section>
  )
}

/*
  Состав должников по счёту. Доля показывается только там, где она есть:
  «солидарно» и «доля 1/2» - разные основания требования, и второе без числа
  бессмысленно (§6.9).
*/
function Debtors({ debtors }) {
  if (debtors.length === 0) {
    return <span className="muted">состав не загружен из выгрузки</span>
  }
  return (
    <ul className="list">
      {debtors.map((debtor) => (
        <li key={debtor.person_id}>
          {debtor.full_name} - {PERSON_ROLE_TITLES[debtor.role]}
          {debtor.share ? `, доля ${percent(debtor.share)}` : ''}
        </li>
      ))}
    </ul>
  )
}
